/**
 * TaskView（パイロット側ソフト）の課題表を手入力する機能の、DOMに依存しない部分。
 *
 * パイロットからは写真・スクリーンショットでしか届かない（docs/08参照）ため、
 * クルーが画面を見ながら1件ずつ入力する想定。座標は次の3通りで受け付ける:
 *   - 3桁の数字 … 会場の公式ゴール番号（ゴール一覧の番号入力と同じ規約、
 *     原典互換。HWZ課題はこの番号でゴールを指定することが多い）
 *   - グリッド表記の数字列（会場のUTM表記設定に従う桁数）
 *   - 「緯度, 経度」
 * 地図にはRmin〜Rmaxの二重円として描く（$_TVCIRCLEの表示と同じ流儀・
 * 同じ`CircleArea`型）。
 */

import { parseLatLon, type CircleArea, type MarkPoint } from '@gv4d/core';
import { gridCodeDigitCount, resolveGridCode } from '../goals.ts';

export interface TaskCircleGoal {
  name: string;
  lat: number;
  lon: number;
  altFt: number | null;
  color: string;
  rMinKm: number;
  rMaxKm: number;
}

/** 選べる色（TaskView画面で見られる色に近いものをプリセットとして用意） */
export const TASK_CIRCLE_COLOR_PRESETS: Array<{ value: string; label: string; name: string }> = [
  { value: '#ff8c00', label: 'オレンジ', name: 'orange' },
  { value: '#ff69b4', label: 'ピンク', name: 'pink' },
  { value: '#1f6fff', label: '青', name: 'blue' },
  { value: '#2ecc71', label: '緑', name: 'green' },
  { value: '#e74c3c', label: '赤', name: 'red' },
];

const COLOR_NAME_TO_HEX: Record<string, string> = Object.fromEntries(
  TASK_CIRCLE_COLOR_PRESETS.map((c) => [c.name, c.value]),
);

/**
 * 色の指定を解決する。`#hex`はそのまま、プリセット色名（大文字小文字を
 * 問わない。OCR結果の"Orange"等をそのまま受けられるように）はhexへ、
 * それ以外・未指定は既定色（先頭のプリセット）にする。
 */
export function resolveTaskCircleColor(value: string | undefined | null): string {
  const fallback = TASK_CIRCLE_COLOR_PRESETS[0].value;
  if (!value) return fallback;
  const v = value.trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(v)) return v;
  return COLOR_NAME_TO_HEX[v.toLowerCase()] ?? fallback;
}

/**
 * 座標の手入力テキストを解決する。カンマを含めば「緯度, 経度」形式、
 * 3桁の数字なら会場のゴール番号（`venueGoals`から`name`が一致するものを
 * 探す。ゴール一覧の番号入力`setupGoalEntry()`と同じ規約）、それ以外は
 * 現在のグリッド表記設定（8〜10桁）の数字列として解釈する。読み取れ
 * なければnull。
 */
export function resolveTaskCircleCode(
  code: string,
  ref: { lat: number; lon: number },
  wideEasting: boolean,
  wideNorthing: boolean,
  venueGoals: MarkPoint[] = [],
): { lat: number; lon: number } | null {
  if (code.includes(',')) return parseLatLon(code);
  const digits = code.replace(/\D/g, '');
  if (digits.length === 3) {
    const mark = venueGoals.find((g) => g.name === digits);
    return mark ? { lat: mark.lat, lon: mark.lon } : null;
  }
  if (digits.length !== gridCodeDigitCount(wideEasting, wideNorthing)) return null;
  return resolveGridCode(digits, ref, wideEasting, wideNorthing);
}

/** JSONインポート1件ぶんの、想定するキー（すべて緩く受ける。無くてもエラーにしない） */
export interface TaskCircleImportEntry {
  name?: unknown;
  code?: unknown;
  altFt?: unknown;
  color?: unknown;
  rMinKm?: unknown;
  rMaxKm?: unknown;
}

export interface TaskCircleImportResult {
  goals: TaskCircleGoal[];
  /** 読み取れなかった項目（1件の異常で全体を止めないよう、続行しつつ積む） */
  skipped: Array<{ name: string; reason: string }>;
}

/**
 * OCR等で読み取ったJSON配列を課題ゴールへ変換する。
 *
 * 手入力（`resolveTaskCircleCode`）と全く同じ座標解決を使うので、コード名の
 * 数字列でも「緯度, 経度」でも読める。1件ごとに独立して検証し、読めない
 * 行があっても他の行は続けて取り込む（写真のOCR結果は一部だけ読み違えて
 * いることがあるため、1件の不備で全滅させない）。
 */
export function parseTaskCircleImport(
  json: unknown,
  ref: { lat: number; lon: number },
  wideEasting: boolean,
  wideNorthing: boolean,
  venueGoals: MarkPoint[] = [],
): TaskCircleImportResult {
  const goals: TaskCircleGoal[] = [];
  const skipped: Array<{ name: string; reason: string }> = [];
  if (!Array.isArray(json)) {
    return { goals, skipped: [{ name: '(全体)', reason: 'JSONの内容が配列ではありません' }] };
  }
  for (const raw of json) {
    const e = raw as TaskCircleImportEntry | null;
    const name = typeof e?.name === 'string' ? e.name.trim() : '';
    if (!name) {
      skipped.push({ name: '(無名の項目)', reason: '名前(name)がありません' });
      continue;
    }
    const code = typeof e?.code === 'string' ? e.code.trim() : '';
    if (!code) {
      skipped.push({ name, reason: '座標(code)がありません' });
      continue;
    }
    const p = resolveTaskCircleCode(code, ref, wideEasting, wideNorthing, venueGoals);
    if (!p) {
      skipped.push({ name, reason: `座標を読み取れません（${code}）` });
      continue;
    }
    goals.push({
      name,
      lat: p.lat,
      lon: p.lon,
      altFt: typeof e?.altFt === 'number' && Number.isFinite(e.altFt) ? e.altFt : null,
      color: resolveTaskCircleColor(typeof e?.color === 'string' ? e.color : undefined),
      rMinKm: typeof e?.rMinKm === 'number' && Number.isFinite(e.rMinKm) ? e.rMinKm : 0,
      rMaxKm: typeof e?.rMaxKm === 'number' && Number.isFinite(e.rMaxKm) ? e.rMaxKm : 0,
    });
  }
  return { goals, skipped };
}

/** 課題表のゴールを、地図に描く競技エリア（円）へ変換する */
export function taskCirclesToAreas(goals: TaskCircleGoal[]): CircleArea[] {
  const areas: CircleArea[] = [];
  for (const g of goals) {
    if (g.rMaxKm > 0) {
      areas.push({
        group: g.name,
        id: `${g.name}_max`,
        lat: g.lat,
        lon: g.lon,
        radius: g.rMaxKm * 1000,
        color: g.color,
      });
    }
    if (g.rMinKm > 0) {
      areas.push({
        group: g.name,
        id: `${g.name}_min`,
        lat: g.lat,
        lon: g.lon,
        radius: g.rMinKm * 1000,
        color: g.color,
      });
    }
  }
  return areas;
}
